// app/features/industry-opportunities/components/OpportunitySolutionSectionComponent.tsx
'use client';

import { AlertTriangle, Bot, Brain, CheckCircle, Cog, Database, MessageSquare, Target,Users } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { IndustryOpportunity } from '../industry-opportunity.interface';

interface OpportunitySolutionSectionComponentProps {
  opportunity: IndustryOpportunity;
}

// Types for solution components from metadata_json
interface AIAgentComponent {
  type: 'AIAgent';
  title: string;
  description: string;
  capabilities: string[];
}

interface HumanReviewComponent {
  type: 'HumanReview';
  title: string;
  description: string;
  review_criteria: string[];
  available_actions: string[];
}

interface InternalSoftwareComponent {
  type: 'InternalSoftware';
  title: string;
  description: string;
  capabilities: string[];
}

type SolutionComponent = AIAgentComponent | HumanReviewComponent | InternalSoftwareComponent;

// Get solution components from metadata_json
function getSolutionComponents(opportunity: IndustryOpportunity): SolutionComponent[] {
  const metadata = opportunity.metadata_json as any;
  
  // Check if solution_components exists in metadata
  if (metadata?.solution_components && Array.isArray(metadata.solution_components)) {
    return metadata.solution_components;
  }
  
  // Fallback: parse solution_description into a basic AIAgent component
  // This handles existing data that doesn't have solution_components yet
  if (opportunity.solution_description) {
    const capabilities = opportunity.solution_description
      .split(/[.!]/)
      .map(s => s.trim())
      .filter(s => s.length > 20 && s.length < 250)
      .slice(0, 4);
    
    return [{
      type: 'AIAgent',
      title: 'AI Automation',
      description: 'How our AI solves this problem for you',
      capabilities
    }];
  }
  
  return [];
}

// Component styles
const STYLES = {
  AIAgent: { 
    accentBgClass: 'bg-purple-500', 
    iconContainerBgClass: 'bg-purple-100', 
    iconTextColorClass: 'text-purple-600', 
    iconBorderClass: 'border-purple-200',
    badgeText: 'AI Agent'
  },
  HumanReview: { 
    accentBgClass: 'bg-teal', 
    iconContainerBgClass: 'bg-teal/20', 
    iconTextColorClass: 'text-teal', 
    iconBorderClass: 'border-teal/30',
    badgeText: 'Human Review'
  },
  InternalSoftware: {
    accentBgClass: 'bg-indigo',
    iconContainerBgClass: 'bg-indigo/15',
    iconTextColorClass: 'text-indigo',
    iconBorderClass: 'border-indigo/30',
    badgeText: 'Internal Software'
  },
};

// Action icon mapping
const ACTION_ICONS: Record<string, React.ElementType> = {
  'Approve': CheckCircle,
  'Reject': AlertTriangle,
  'RequestRevision': MessageSquare,
  'LogFeedback': MessageSquare,
  'Edit': MessageSquare,
};

export function OpportunitySolutionSectionComponent({ 
  opportunity 
}: OpportunitySolutionSectionComponentProps) {
  const components = getSolutionComponents(opportunity);
  
  if (components.length === 0 && !opportunity.solution_description) {
    return null;
  }

  return (
    <div className="py-12 lg:py-16 bg-beige">
      <div className="container mx-auto px-4 md:px-6">
        <div className="lg:flex lg:gap-x-12 xl:gap-x-16">
          {/* Left Sticky Column */}
          <aside className="lg:w-1/3 xl:max-w-sm lg:sticky lg:top-24 self-start mb-10 lg:mb-0">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-6">
              The Solution
            </h2>
            
            {(() => {
              const cardDesc = opportunity.metadata_json && 
                typeof opportunity.metadata_json === 'object' && 
                'card_description' in opportunity.metadata_json
                ? String(opportunity.metadata_json.card_description)
                : null;
              return cardDesc && (
                <p className="text-xl text-gray-700 leading-relaxed">
                  {cardDesc}
                </p>
              );
            })()}
          </aside>

          {/* Right Content Column */}
          <main className="lg:w-2/3 xl:flex-grow space-y-6">
            {components.map((component, index) => {
              if (component.type === 'AIAgent') {
                return <AIAgentCard key={index} component={component} />;
              }
              if (component.type === 'HumanReview') {
                return <HumanReviewCard key={index} component={component} />;
              }
              if (component.type === 'InternalSoftware') {
                return <InternalSoftwareCard key={index} component={component} />;
              }
              return null;
            })}
          </main>
        </div>
      </div>
    </div>
  );
}

// AI Agent Card Component
function AIAgentCard({ component }: { component: AIAgentComponent }) {
  const style = STYLES.AIAgent;
  
  return (
    <div className="bg-[#F0EBE6] rounded-lg shadow-lg overflow-hidden">
      <div className={cn("h-1.5", style.accentBgClass)} />
      
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-start mb-6">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 border",
            style.iconContainerBgClass,
            style.iconBorderClass
          )}>
            <Bot size={24} className={style.iconTextColorClass} />
          </div>
          <div className="flex-grow">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{component.title}</h3>
              <span className={cn(
                "text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap text-white",
                style.accentBgClass
              )}>
                {style.badgeText}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{component.description}</p>
          </div>
        </div>

        {/* Capabilities */}
        {component.capabilities && component.capabilities.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono flex items-center gap-1.5">
                <Brain size={16} className="text-purple-500" /> 
                What The AI Does
              </h4>
            </div>
            <div className="space-y-3 pl-2">
              {component.capabilities.map((capability, idx) => (
                <div key={idx} className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-6 h-6 rounded-md bg-purple-100 border border-purple-200 flex items-center justify-center text-xs text-purple-700 font-mono mt-0.5">
                    {idx + 1}
                  </div>
                  <p className="text-base text-gray-700">{capability}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Internal Software Card Component
function InternalSoftwareCard({ component }: { component: InternalSoftwareComponent }) {
  const style = STYLES.InternalSoftware;

  return (
    <div className="bg-[#F0EBE6] rounded-lg shadow-lg overflow-hidden">
      <div className={cn("h-1.5", style.accentBgClass)} />

      <div className="p-4 md:p-6">
        <div className="flex items-start mb-6">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 border",
            style.iconContainerBgClass,
            style.iconBorderClass
          )}>
            <Database size={24} className={style.iconTextColorClass} />
          </div>
          <div className="flex-grow">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{component.title}</h3>
              <span className={cn(
                "text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap text-white",
                style.accentBgClass
              )}>
                {style.badgeText}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{component.description}</p>
          </div>
        </div>

        {component.capabilities && component.capabilities.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono flex items-center gap-1.5">
                <Cog size={16} className="text-indigo" />
                What The Software Does
              </h4>
            </div>
            <div className="space-y-3 pl-2">
              {component.capabilities.map((capability, idx) => (
                <div key={idx} className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-6 h-6 rounded-md bg-indigo/15 border border-indigo/30 flex items-center justify-center text-xs text-indigo font-mono mt-0.5">
                    {idx + 1}
                  </div>
                  <p className="text-base text-gray-700">{capability}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Human Review Card Component
function HumanReviewCard({ component }: { component: HumanReviewComponent }) {
  const style = STYLES.HumanReview;
  
  return (
    <div className="bg-[#F0EBE6] rounded-lg shadow-lg overflow-hidden">
      <div className={cn("h-1.5", style.accentBgClass)} />
      
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-start mb-6">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 border",
            style.iconContainerBgClass,
            style.iconBorderClass
          )}>
            <Users size={24} className={style.iconTextColorClass} />
          </div>
          <div className="flex-grow">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{component.title}</h3>
              <span className={cn(
                "text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap text-white",
                style.accentBgClass
              )}>
                {style.badgeText}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{component.description}</p>
          </div>
        </div>

        {/* Available Actions */}
        {component.available_actions && component.available_actions.length > 0 && (
          <div className="bg-beige-dark my-4 rounded-md p-3 shadow-inner flex flex-col md:flex-row justify-around items-center gap-4 md:gap-0">
            {component.available_actions.map((action, idx) => {
              const IconComponent = ACTION_ICONS[action] || MessageSquare;
              return (
                <div key={idx} className="flex flex-col items-center text-center">
                  <span className="text-gray-700 text-xs font-mono uppercase mb-1 flex items-center gap-1.5">
                    <IconComponent size={16} /> ACTION {idx + 1}
                  </span>
                  <div className="text-base font-medium">
                    {action}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Review Criteria */}
        {component.review_criteria && component.review_criteria.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono flex items-center gap-1.5">
                <Target size={16} className="text-teal" /> 
                Review Criteria
              </h4>
            </div>
            <div className="space-y-2">
              {component.review_criteria.map((criterion, idx) => (
                <div key={idx} className="text-md text-gray-700 flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 mr-2 flex-shrink-0" />
                  {criterion}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

